import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import InsightButtonCard from '../InsightButtonCard/InsightButtonCard';
import Modal from '../common/Modal/Modal';
import { BiomassOflAbcChart } from '../FisheryCharts/FisheryCharts';
import type { RegistryComponentProps } from '../../pages/registry/componentRegistry';
import { getRegistryData } from '../../pages/registry/componentRegistry';
import { usePageDataContextOptional } from '../../contexts/PageDataContext';

interface AbcOflLandingsCardData {
  title?: string;
  buttonLabel?: string;
  timeSeriesData?: { biomass?: Array<Record<string, unknown>> };
  loading?: boolean;
  error?: string | null;
}

function isAllZero(
  data: Array<Record<string, unknown>> | undefined,
  keys: string[] = ['biomass', 'ofl', 'abc', 'landings']
): boolean {
  if (!data?.length) return true;
  return data.every((d) =>
    keys.every((key) => {
      const val = d[key];
      return val === 0 || val === null || val === undefined;
    })
  );
}

export default function AbcOflLandingsCard(props: RegistryComponentProps): React.ReactElement {
  const [showOflAbcModal, setShowOflAbcModal] = useState(false);
  const pageData = usePageDataContextOptional();
  const injectionData = getRegistryData<AbcOflLandingsCardData>(props);

  const timeSeriesData =
    (pageData?.data?.timeSeriesData as AbcOflLandingsCardData['timeSeriesData']) ??
    injectionData.timeSeriesData;
  const loading = pageData?.loading ?? injectionData.loading ?? false;
  const error = pageData?.error ?? injectionData.error ?? null;
  const title = injectionData.title ?? 'ABC/OFL/Landings';
  const buttonLabel = injectionData.buttonLabel ?? 'Historic ABC/OFL/Landings';

  const biomassData = timeSeriesData?.biomass ?? [];
  const filteredData = (biomassData as Array<{ year: number }>).filter((item) => item.year <= 2024);
  const hasData =
    biomassData.length > 0 && !isAllZero(biomassData as Array<Record<string, unknown>>);

  return (
    <>
      <InsightButtonCard
        title={title}
        buttonLabel={buttonLabel}
        onClick={() => setShowOflAbcModal(true)}
      />
      {showOflAbcModal &&
        createPortal(
          <Modal
            title="OFL/ABC/LANDINGS: All Stocks (Historical)"
            onClose={() => setShowOflAbcModal(false)}
            size="extra-large"
            transparentBackdrop={true}
            parameterStyle={true}
            {...({ customMaxWidth: '90vw', customMaxHeight: '90vh' } as Record<string, unknown>)}
            bodyStyle={{ padding: '20px' }}
          >
            {loading ? (
              <p className="noscroll-loading-message">Loading data...</p>
            ) : error ? (
              <p className="noscroll-error-message">{error}</p>
            ) : !hasData ? (
              <p className="noscroll-no-data-message">Data deficient</p>
            ) : (
              <BiomassOflAbcChart
                {...({ data: filteredData, maxHeight: 400 } as React.ComponentProps<typeof BiomassOflAbcChart>)}
              />
            )}
          </Modal>,
          document.body
        )}
    </>
  );
}
